import React, { useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { ExternalLink, MessageCircle } from 'lucide-react'
import type { CuratedFeedItem } from '@/services/curatedFeedService'
import CuratedCommentThread from './CuratedCommentThread'
import CuratedReactionBar from './CuratedReactionBar'

interface CuratedItemCardProps {
  item: CuratedFeedItem
}

const CuratedItemCard: React.FC<CuratedItemCardProps> = ({ item }) => {
  const [showComments, setShowComments] = useState(false)
  const [commentCount, setCommentCount] = useState(item.commentCount)

  return (
    <article className="flex h-full flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex gap-3">
        {item.thumbnailUrl && (
          <img
            src={item.thumbnailUrl}
            alt=""
            className="h-20 w-20 flex-shrink-0 rounded-md object-cover"
          />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-xs text-gray-500">
            {item.sourceName} · {formatDistanceToNow(new Date(item.publishedAt), { addSuffix: true })}
          </p>
          <h2 className="mt-1 text-sm font-semibold text-gray-900">{item.title}</h2>
          {item.summary && (
            <p className="mt-1 line-clamp-3 text-sm text-gray-600">{item.summary}</p>
          )}
          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Read full article
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>

      <footer className="mt-auto space-y-3 pt-3">
        <div className="flex items-center justify-between">
          <CuratedReactionBar
            curatedItemId={item.id}
            reactionCounts={item.reactionCounts}
            viewerReaction={item.viewerReaction}
          />
          <button
            type="button"
            onClick={() => setShowComments((v) => !v)}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <MessageCircle className="h-4 w-4" />
            {commentCount}
          </button>
        </div>

        {showComments && (
          <CuratedCommentThread curatedItemId={item.id} onCountChange={setCommentCount} />
        )}
      </footer>
    </article>
  )
}

export default CuratedItemCard
